import { Rng } from '../core/rng';
import { clamp, wrapAngle } from '../core/math';
import { awarenessRate, sightRange, type SightConditions } from './detection';
import type { SimEvents } from './events';
import type { Vessel } from './vessel';

// The player's lookouts. They only know about ships they have actually
// seen, by the same rules the enemy captains see by (sightRange). While a
// ship is in sight her bearing is taken by compass and her range, course
// and speed are judged by eye from her real position, with errors that grow
// with distance. Once she's lost to sight she's dead-reckoned from the last
// estimate, the doubt growing, until the lookouts give her up.

/** What the lookouts make of one ship. Positions are world metres. */
export interface Sighting {
  vessel: number;
  /** Estimated position and velocity. */
  x: number;
  z: number;
  vx: number;
  vz: number;
  /** Estimated heading (radians, clockwise from north). */
  heading: number;
  /** Rate the range is opening (+) or closing (-), m/s, smoothed. */
  rangeRate: number;
  /** Detection progress; she's in sight at >= 1. */
  awareness: number;
  visible: boolean;
  /** World time first and last seen. */
  firstSeen: number;
  seen: number;
  /** Close enough (once) to make out her colours, rig and name. */
  identified: boolean;
  /** One-sigma doubt about the position, m. */
  uncertainty: number;
  /** Seconds she's been watched continuously (course and speed need a while). */
  watched: number;
  /** Slowly wandering errors in the judged range and heading. */
  rangeErr: number;
  headErr: number;
  speedErr: number;
  /** Gone for good (sunk or given up): kept briefly so the HUD can say so. */
  gone: boolean;
  goneAt: number;
}

/** Seconds out of sight before the lookouts give a ship up. */
export const GIVE_UP = 360;
const TICK = 0.5;

export class Lookout {
  readonly sightings = new Map<number, Sighting>();
  private readonly rng: Rng;
  private timer = 0;

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  /** Sightings the player may know about: seen at least once, not given up. */
  known(): Sighting[] {
    const out: Sighting[] = [];
    for (const s of this.sightings.values()) if (!s.gone && s.firstSeen >= 0) out.push(s);
    return out;
  }

  /** The sighting of a vessel, if the lookouts have ever actually seen her. */
  get(vessel: number): Sighting | undefined {
    const s = this.sightings.get(vessel);
    return s && s.firstSeen >= 0 ? s : undefined;
  }

  private lose(s: Sighting, time: number): void {
    if (s.gone) return;
    s.gone = true;
    s.goneAt = time;
    s.visible = false;
  }

  update(dt: number, me: Vessel, vessels: readonly Vessel[], c: SightConditions & { time: number }, events: SimEvents): void {
    // Between observations every estimate runs on at its judged velocity.
    for (const s of this.sightings.values()) {
      s.x += s.vx * dt;
      s.z += s.vz * dt;
    }
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = TICK;
    const px = me.ship.com.x, pz = me.ship.com.z;
    for (const o of vessels) {
      if (o === me) continue;
      let s = this.sightings.get(o.id);
      if (!o.alive) {
        if (s) this.lose(s, c.time);
        continue;
      }
      const dx = o.ship.com.x - px, dz = o.ship.com.z - pz;
      const d = Math.hypot(dx, dz);
      const range = sightRange(me, o, c);
      if (!s) {
        // Not a word about ships nobody aboard has laid eyes on.
        if (d >= range) continue;
        s = {
          vessel: o.id, x: o.ship.com.x, z: o.ship.com.z, vx: 0, vz: 0, heading: o.ship.heading, rangeRate: 0,
          awareness: 0, visible: false, firstSeen: -1, seen: -1e9, identified: false, uncertainty: 0, watched: 0,
          rangeErr: this.rng.gauss() * 0.015, headErr: this.rng.gauss() * 0.04, speedErr: this.rng.gauss() * 0.05, gone: false, goneAt: 0,
        };
        this.sightings.set(o.id, s);
      }
      if (s.gone) continue;
      if (d < range) s.awareness = Math.min(1.5, s.awareness + awarenessRate(d, range, me.crew.gunnery) * TICK);
      else s.awareness = Math.max(0, s.awareness - TICK * 0.05);
      const nowVisible = s.awareness >= 1 && d < range * 1.1;
      if (nowVisible) {
        // Bearing by compass is good; range, course and speed are judged,
        // worse the further off she is.
        const far = clamp(d / Math.max(range, 1), 0, 1.2);
        s.rangeErr = clamp(s.rangeErr * 0.97 + this.rng.gauss() * 0.002, -0.06, 0.06);
        s.headErr = clamp(s.headErr * 0.95 + this.rng.gauss() * 0.01, -0.15, 0.15);
        s.speedErr = clamp(s.speedErr * 0.95 + this.rng.gauss() * 0.01, -0.15, 0.15);
        const judged = d * (1 + s.rangeErr * (0.3 + 2 * far));
        const ox = px + (dx / (d || 1)) * judged, oz = pz + (dz / (d || 1)) * judged;
        if (!s.visible) {
          if (s.firstSeen < 0) {
            s.firstSeen = c.time;
            events.push({ type: 'sighted', vessel: me.id, target: o.id });
          }
          s.x = ox;
          s.z = oz;
          s.watched = 0;
        } else {
          s.x += (ox - s.x) * 0.5;
          s.z += (oz - s.z) * 0.5;
        }
        s.heading = wrapAngle(o.ship.heading + s.headErr * (0.3 + far));
        // Her way through the water, judged from the bow wave and the sail she
        // carries: the real motion, turned and scaled by the judgement error.
        const turn = s.headErr * (0.3 + far), k = 1 + s.speedErr * (0.3 + far);
        const cs = Math.cos(turn), sn = Math.sin(turn);
        const tvx = (o.ship.vel.x * cs - o.ship.vel.z * sn) * k, tvz = (o.ship.vel.x * sn + o.ship.vel.z * cs) * k;
        const blend = s.watched < TICK ? 1 : 0.3;
        s.vx += (tvx - s.vx) * blend;
        s.vz += (tvz - s.vz) * blend;
        // Opening or closing: her judged motion against our own, along the bearing.
        const rr = ((s.vx - me.ship.vel.x) * dx + (s.vz - me.ship.vel.z) * dz) / (d || 1);
        s.rangeRate += (rr - s.rangeRate) * (s.watched < TICK ? 1 : 0.3);
        s.watched += TICK;
        s.seen = c.time;
        s.uncertainty = 8 + d * 0.035 * far;
        const identify = Math.min(range * 0.55, 4600) * (c.daylight > 0.3 ? 1 : 0.3);
        if (d < identify) s.identified = true;
      } else if (s.seen > -1e8) {
        // Lost: dead reckoning, with the doubt growing by the minute.
        s.uncertainty += TICK * (1.2 + Math.hypot(s.vx, s.vz) * 0.25);
        s.rangeRate = 0;
        if (c.time - s.seen > GIVE_UP) this.lose(s, c.time);
      }
      s.visible = nowVisible;
    }
    // Ships no longer on the water at all; long-forgotten ones are dropped.
    for (const [id, s] of this.sightings) {
      if (!s.gone && !vessels.some((v) => v.id === id)) this.lose(s, c.time);
      if (s.gone && c.time - s.goneAt > 30) this.sightings.delete(id);
      // Glimpsed for a moment but never made out: forget it once far off.
      if (s.firstSeen < 0 && s.awareness <= 0) this.sightings.delete(id);
    }
  }
}
